/**
 * MassnahmenScheduleDialog
 *
 * Massen-Editor fuer den Wochenplan aller Massnahmen eines Standorts.
 *
 * Tabelle: Zeilen = Massnahmen, Spalten = Kalenderwochen.
 * Jede Zelle ist ein Toggle (gruen = aktiv, rot = inaktiv).
 *
 * Editier-Semantik (spiegelt das vorhandene Modell ``scheduled_weeks`` /
 * ``excluded_weeks`` und die Renderer-Logik in Planung, TV-View und
 * Excel-Export):
 *
 *   * Hat eine Massnahme `scheduled_weeks` nicht-leer → "Whitelist-Modus":
 *     ein Klick auf eine Woche fuegt sie hinzu oder entfernt sie aus
 *     `scheduled_weeks`.
 *   * Sonst → "Blacklist-Modus": Klick toggelt die Woche in `excluded_weeks`.
 *
 * Damit bleibt der bestehende User-Workflow (im Massnahme-Detail Dialog
 * konfiguriert ein User ggf. Whitelist oder Blacklist) erhalten und die
 * Anzeige in der Wochenanzeige aktualisiert sich automatisch.
 *
 * Aenderungen werden direkt per PUT /api/massnahmen/{id} persistiert
 * (optimistic UI). Nach erfolgreichem Save loest die Komponente
 * `onChange()` aus, damit die Eltern-View die Liste neu laedt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { startOfWeek, addWeeks, getISOWeek, format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { CalendarRange, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { massnahmenAPI } from '../../lib/api';

const WEEKS_AT_ONCE = 12;

function getWeekKey(d) {
  const year = d.getFullYear();
  const kw = getISOWeek(d);
  // ISO-Korrektur: Wochen am Jahreswechsel koennen zum vorherigen Jahr gehoeren
  // (z.B. 01.01. liegt oft in KW 53 des Vorjahres).
  const safeYear = (kw >= 52 && d.getMonth() === 0) ? year - 1 : year;
  return `${safeYear}-W${String(kw).padStart(2, '0')}`;
}

function buildWeeks(startIdx, count) {
  const today = new Date();
  const monday0 = startOfWeek(today, { weekStartsOn: 1 });
  return Array.from({ length: count }, (_, i) => {
    const m = addWeeks(monday0, startIdx + i);
    return {
      monday: m,
      key: getWeekKey(m),
      label: `KW ${getISOWeek(m)}`,
      sublabel: format(m, 'dd.MM.', { locale: de }),
    };
  });
}

function isMassnahmeActiveInWeek(m, weekKey) {
  const scheduled = m.scheduled_weeks || [];
  const excluded = m.excluded_weeks || [];
  if (scheduled.length > 0) return scheduled.includes(weekKey);
  if (excluded.includes(weekKey)) return false;
  return true;
}

function toggleMassnahmeWeek(m, weekKey) {
  const scheduled = m.scheduled_weeks || [];
  const excluded = m.excluded_weeks || [];
  const currentlyActive = isMassnahmeActiveInWeek(m, weekKey);
  // Whitelist-Modus (scheduled_weeks gesetzt) → in scheduled_weeks toggeln
  if (scheduled.length > 0) {
    const next = currentlyActive
      ? scheduled.filter((k) => k !== weekKey)
      : [...scheduled, weekKey];
    return { scheduled_weeks: next };
  }
  // Blacklist-Modus → in excluded_weeks toggeln
  const next = currentlyActive
    ? [...excluded, weekKey]
    : excluded.filter((k) => k !== weekKey);
  return { excluded_weeks: next };
}

export default function MassnahmenScheduleDialog({
  open, onOpenChange, massnahmen, canManage = true, onChange,
}) {
  const [startIdx, setStartIdx] = useState(0);
  // Lokaler State spiegelt die Massnahmen — wird nach erfolgreichem PUT aktualisiert,
  // damit die Tabelle reaktiv neu rendert ohne ein parent-reload abzuwarten.
  const [localMassnahmen, setLocalMassnahmen] = useState(massnahmen);
  const [savingCell, setSavingCell] = useState(null); // `${id}|${weekKey}` waehrend Speichern

  useEffect(() => {
    // Synchronisiere lokalen State nur, wenn sich die schedule-relevanten Felder
    // tatsaechlich veraendert haben — eine reine Referenz-Aenderung des Arrays
    // (durch sortedMassnahmen-Re-Compute im Parent) soll den optimistischen
    // Patch NICHT zuruecksetzen, sonst wird ein gerade gespeicherter Toggle wieder
    // ueberschrieben (siehe iteration_68.json RCA).
    setLocalMassnahmen((prev) => {
      if (prev === massnahmen) return prev;
      if (prev.length !== massnahmen.length) return massnahmen;
      const sig = (m) => `${m.id}|${(m.scheduled_weeks || []).join(',')}|${(m.excluded_weeks || []).join(',')}`;
      const same = prev.every((m, i) => sig(m) === sig(massnahmen[i]));
      return same ? prev : massnahmen;
    });
  }, [massnahmen]);
  useEffect(() => { if (!open) setStartIdx(0); }, [open]);

  const weeks = useMemo(() => buildWeeks(startIdx, WEEKS_AT_ONCE), [startIdx]);

  const handleToggle = async (m, weekKey) => {
    if (!canManage) return;
    const cellKey = `${m.id}|${weekKey}`;
    if (savingCell) return; // Doppelklicks ignorieren
    setSavingCell(cellKey);
    const patch = toggleMassnahmeWeek(m, weekKey);
    // Optimistic update
    setLocalMassnahmen((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...patch } : x)));
    try {
      await massnahmenAPI.update(m.id, patch);
      // Best-effort parent-refresh, damit Planungs- und TV-Ansicht aktualisieren
      onChange?.();
    } catch (err) {
      // Rollback bei Fehler
      setLocalMassnahmen((prev) => prev.map((x) => (x.id === m.id ? m : x)));
      const msg = err?.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : 'Speichern fehlgeschlagen');
    } finally {
      setSavingCell(null);
    }
  };

  const totalActiveForMassnahme = (m) => weeks.filter((w) => isMassnahmeActiveInWeek(m, w.key)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(96vw,1400px)] max-h-[90vh] overflow-hidden flex flex-col"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            Wochenplan – Massnahmen
          </DialogTitle>
          <DialogDescription>
            Pro Maßnahme festlegen, in welchen Kalenderwochen sie aktiv ist.
            Änderungen erscheinen sofort in der Planungsansicht, TV-Ansicht und im Excel-Export.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between border-b border-stone-200 py-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStartIdx((i) => i - WEEKS_AT_ONCE)}
              data-testid="ms-week-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStartIdx(0)}
              disabled={startIdx === 0}
            >
              Heute
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStartIdx((i) => i + WEEKS_AT_ONCE)}
              data-testid="ms-week-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {weeks[0].label} – {weeks[weeks.length - 1].label} · {format(weeks[0].monday, 'dd.MM.yyyy', { locale: de })} bis {format(addWeeks(weeks[weeks.length - 1].monday, 0), 'dd.MM.yyyy', { locale: de })}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="min-w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white z-10 border-b-2 border-stone-300">
              <tr>
                <th className="text-left p-2 font-semibold text-stone-700 sticky left-0 bg-white z-20 border-r border-stone-200 min-w-[200px]">
                  Maßnahme
                </th>
                {weeks.map((w) => (
                  <th
                    key={w.key}
                    className="p-1 font-medium text-stone-600 text-center text-xs whitespace-nowrap"
                    title={w.key}
                  >
                    <div>{w.label}</div>
                    <div className="text-[10px] text-stone-400 font-normal">{w.sublabel}</div>
                  </th>
                ))}
                <th className="p-2 text-center text-xs text-stone-600 sticky right-0 bg-white z-20 border-l border-stone-200">
                  Aktiv
                </th>
              </tr>
            </thead>
            <tbody>
              {localMassnahmen.length === 0 && (
                <tr>
                  <td colSpan={weeks.length + 2} className="p-8 text-center text-stone-500">
                    Keine Maßnahmen vorhanden.
                  </td>
                </tr>
              )}
              {localMassnahmen.map((m) => (
                <tr key={m.id} className="border-b border-stone-100 hover:bg-stone-50/60">
                  <td className="p-2 sticky left-0 bg-white border-r border-stone-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-7 h-7 shrink-0 rounded text-white text-xs font-bold flex items-center justify-center"
                        style={{ backgroundColor: m.color || '#14532D' }}
                      >
                        {(m.short_code || m.name || '').slice(0, 3)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{m.name}</div>
                        <div className="text-[10px] text-stone-500 truncate">
                          {(m.scheduled_weeks || []).length > 0 ? 'Whitelist-Modus' : 'Standard (immer aktiv)'}
                        </div>
                      </div>
                    </div>
                  </td>
                  {weeks.map((w) => {
                    const active = isMassnahmeActiveInWeek(m, w.key);
                    const isSaving = savingCell === `${m.id}|${w.key}`;
                    return (
                      <td key={w.key} className="p-1 text-center">
                        <button
                          type="button"
                          disabled={!canManage || isSaving}
                          onClick={() => handleToggle(m, w.key)}
                          data-testid={`ms-cell-${m.id}-${w.key}`}
                          className={`w-8 h-8 rounded-md inline-flex items-center justify-center transition-all ${
                            active
                              ? 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 ring-1 ring-emerald-500/30'
                              : 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 ring-1 ring-rose-500/30'
                          } ${isSaving ? 'opacity-50' : ''} ${!canManage ? 'cursor-not-allowed' : ''}`}
                          title={active ? `Aktiv in ${w.key} (klicken zum Deaktivieren)` : `Inaktiv in ${w.key} (klicken zum Aktivieren)`}
                        >
                          {active ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                      </td>
                    );
                  })}
                  <td className="p-2 text-center font-medium text-stone-600 sticky right-0 bg-white border-l border-stone-200">
                    {totalActiveForMassnahme(m)}/{weeks.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="border-t border-stone-200 pt-3 mt-2">
          <div className="flex-1 flex items-center gap-4 text-xs text-stone-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-500/40 ring-1 ring-emerald-500/40" /> aktiv
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-rose-500/30 ring-1 ring-rose-500/30" /> inaktiv
            </span>
            <span className="hidden md:inline">Änderungen werden automatisch gespeichert.</span>
          </div>
          <Button onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
